import { useEffect, useMemo, useRef } from 'react';

import {
  useGetAccuracyJobsByProjectQuery,
  useGetLatestAccuracyJobsQuery,
} from '../../generate-accuracy/api/accuracyApiSlice';
import {
  ACCURACY_JOB_STATUS,
  ACCURACY_JOB_TERMINAL_STATUSES,
} from '../../generate-accuracy/constants';
import type { AccuracyRunHistoryRow } from '../../generate-accuracy/types/response';

type GetAccuracyJobsByProjectResult = ReturnType<typeof useGetAccuracyJobsByProjectQuery>;

/**
 * Which action the run history row offers:
 * - `cancel` — run is queued or active and can still be cancelled
 * - `cancel-disabled` — run's accuracy numbers are generated and being exported
 *   to the dashboard (`EXPORTING`); cancel is no longer possible
 * - `view-results` — run completed and produced a dashboard iteration
 * - `view-results-disabled` — run completed but has no iteration to open
 * - `none` — failed / cancelled runs have no action
 */
export type AccuracyRunActionType =
  | 'cancel'
  | 'cancel-disabled'
  | 'view-results'
  | 'view-results-disabled'
  | 'none';

export interface AccuracyRunView {
  run: AccuracyRunHistoryRow;
  actionType: AccuracyRunActionType;
}

export interface UseAccuracyRunHistoryResult {
  runs: AccuracyRunView[];
  totalRunCount: number;
  isLoading: boolean;
  isFetching: boolean;
  error: GetAccuracyJobsByProjectResult['error'];
  refetch: GetAccuracyJobsByProjectResult['refetch'];
}

/**
 * Sort rank for the run history list: active runs first, then queued, then
 * everything else in the order the API returned it.
 */
const statusRank = (status: string): number => {
  if (status === ACCURACY_JOB_STATUS.PENDING) return 1;
  if (!ACCURACY_JOB_TERMINAL_STATUSES.has(status)) return 0;
  return 2;
};

const deriveActionType = (run: AccuracyRunHistoryRow): AccuracyRunActionType => {
  if (run.status === ACCURACY_JOB_STATUS.PENDING) return 'cancel';
  // Accuracy numbers are generated and being pushed to the dashboard — the
  // backend rejects a cancel here (409), so surface Cancel as disabled.
  if (run.status === ACCURACY_JOB_STATUS.EXPORTING) return 'cancel-disabled';
  if (!ACCURACY_JOB_TERMINAL_STATUSES.has(run.status)) return 'cancel';
  if (run.status === ACCURACY_JOB_STATUS.COMPLETED) {
    return run.accuracyIterationId ? 'view-results' : 'view-results-disabled';
  }
  return 'none';
};

/**
 * Fetches a project's accuracy run history (`GET /accuracy/jobs/{projectId}`) and
 * returns it in display order with a per-run action derived from status.
 *
 * Stays live by piggy-backing on the Admin Projects screen's centralized
 * latest-jobs poll: this hook subscribes to that same RTK Query cache entry
 * (`getLatestAccuracyJobs`) rather than starting its own timer, and re-pulls the
 * full history each time that poll delivers a fresh tick. That keeps status,
 * progress, ETA, queue positions and queued→in-progress promotions current while
 * the modal is open, without any modal-owned request loop.
 */
// TODO: cleanup with cp_trigger_accuracy_from_ui ff
export const useAccuracyRunHistory = (projectId: string | null): UseAccuracyRunHistoryResult => {
  const { data, isLoading, isFetching, error, refetch } = useGetAccuracyJobsByProjectQuery(
    projectId ?? '',
    { skip: !projectId }
  );

  // Same endpoint/args the screen-level `useAccuracyJobColumn` already polls, so
  // this is a second subscriber to one shared cache entry — no extra timer. Its
  // poll is active whenever any project (including this one, while it has an
  // active or queued run) is non-terminal.
  const { fulfilledTimeStamp: latestJobsTick } = useGetLatestAccuracyJobsQuery(undefined, {
    skip: !projectId,
  });

  const lastAppliedTickRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (!projectId) {
      lastAppliedTickRef.current = undefined;
      return;
    }
    if (latestJobsTick === undefined) {
      return;
    }
    if (lastAppliedTickRef.current === undefined) {
      // First observed tick — the initial history fetch already reflects it.
      lastAppliedTickRef.current = latestJobsTick;
      return;
    }
    if (latestJobsTick !== lastAppliedTickRef.current) {
      lastAppliedTickRef.current = latestJobsTick;
      refetch();
    }
  }, [projectId, latestJobsTick, refetch]);

  const rows = data?.data;

  const runs = useMemo<AccuracyRunView[]>(() => {
    if (!rows) return [];
    return [...rows]
      .sort((a, b) => statusRank(a.status) - statusRank(b.status))
      .map((run) => ({ run, actionType: deriveActionType(run) }));
  }, [rows]);

  return {
    runs,
    totalRunCount: rows?.length ?? 0,
    isLoading,
    isFetching,
    error,
    refetch,
  };
};
